import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

// import LiveChat from './components/LiveChat'; // Disabling LiveChat for CRM cleanup
import Dashboard from './pages/Dashboard';
import DashboardV2 from './pages/DashboardV2';
import RoomDetailV2 from './pages/RoomDetailV2';
import CropsV2 from './pages/CropsV2';
import ClientManagement from './pages/admin/ClientManagement';
import SystemMonitoring from './pages/admin/SystemMonitoring';
import AdminDashboard from './pages/admin/AdminDashboard';
import DeviceInventory from './pages/admin/DeviceInventory';
import Crops from './pages/Crops';
import CropDetail from './pages/CropDetail';
import Rooms from './pages/Rooms';
import RoomDetail from './pages/RoomDetail';
import Genetics from './pages/Genetics';
import { GeneticDetail } from './pages/GeneticDetail';
import GeneticsRD from './pages/GeneticsRD';
import Clones from './pages/Clones';
import { BatchDetail } from './pages/BatchDetail';
import Devices from './pages/Devices';
import { LaboratoryPage } from './pages/LaboratoryPage';
import { ExtractionsPage as Extractions } from './pages/Extractions';




import Stock from './pages/Stock';
import Dispensary from './pages/Dispensary';
import Settings from './pages/Settings';
import Compras from './pages/Compras';
import Insumos from './pages/Insumos';
import { InsumoProviders } from './components/Providers/InsumoProviders'; // New Import
import Expenses from './pages/Expenses';
import Metrics from './pages/Metrics'; // New Import
import Informes from './pages/Informes'; // Added Informes route
import Patients from './pages/Patients';
import PatientDetail from './pages/PatientDetail';
import Templates from './pages/Templates';
import Appointments from './pages/Appointments';
import GrowyDashboard from './pages/GrowyDashboard';
import AccountInfo from './pages/AccountInfo';
import { notificationService } from './services/notificationService';
import Login from './pages/Login';
import Register from './pages/Register'; // New Import
import ForgotPassword from './pages/ForgotPassword';
import UpdatePassword from './pages/UpdatePassword';
import EmailConfirmed from './pages/EmailConfirmed'; // New Import
import { PublicTracking } from './pages/PublicTracking'; // QR Scan Public Route
import PatientOnboarding from './pages/PatientOnboarding'; // Self-Onboarding
import TenantLogin from './pages/public/TenantLogin';
import PatientPortal from './pages/public/PatientPortal';
import { TenantLanding } from './pages/public/TenantLanding';
import TenantApply from './pages/public/TenantApply';
import { useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import { OrganizationProvider, useOrganization } from './context/OrganizationContext';
import './App.css';

import Sidebar, { SidebarProvider } from './components/Sidebar';
import { MobileBottomBar } from './components/Navigation/MobileBottomBar';
import ClickSpark from './components/ClickSpark';
import { GuidedTour } from './components/GuidedTour';

import { ChatWidget } from './components/AI/ChatWidget';
import { GrowyOrb } from './components/GrowyOrb';
import PageTransition from './components/PageTransition';
import { SystemBroadcastBanner } from './components/SystemBroadcastBanner';
import { LoadingSpinner } from './components/LoadingSpinner';
import { MainContent } from './components/MainContent';
import { RoleGuard } from './components/RoleGuard';
import { KYCGuard } from './components/KYCGuard';
import { DemoGuard } from './components/DemoGuard';

const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Init Notifications
    const initNotifications = async () => {
      await notificationService.init();
    };
    initNotifications();
  }, []);

  if (isLoading) return <LoadingSpinner fullScreen duration={3000} />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <PageTransition key={location.key}>
      <DemoGuard>
        {children}
      </DemoGuard>
    </PageTransition>
  );
};

const SessionTimeoutWarning = () => {
  const { isIdleWarningOpen, idleCountdown, continueSession } = useAuth();

  if (!isIdleWarningOpen) return null;

  const minutes = Math.floor(idleCountdown / 60);
  const seconds = (idleCountdown % 60).toString().padStart(2, '0');

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.78)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '20px',
        padding: '2.25rem 2rem',
        maxWidth: '430px',
        width: '90%',
        textAlign: 'center',
        boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.7)',
        color: '#f8fafc'
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⏳</div>
        <h2 style={{ color: '#f8fafc', marginTop: 0, marginBottom: '0.5rem', fontSize: '1.45rem', fontWeight: 800 }}>
          ¿Sigues en consulta?
        </h2>
        <p style={{ color: '#cbd5e1', marginBottom: '1.25rem', lineHeight: 1.5, fontSize: '0.95rem' }}>
          Detectamos inactividad prolongada. Para proteger la privacidad del paciente, la sesión se cerrará en:
        </p>

        <div style={{
          background: 'rgba(15, 23, 42, 0.75)',
          border: '1px solid rgba(245, 158, 11, 0.35)',
          borderRadius: '12px',
          padding: '0.65rem 1.5rem',
          display: 'inline-block',
          marginBottom: '1.25rem',
        }}>
          <span style={{
            color: idleCountdown <= 30 ? '#ef4444' : '#f59e0b',
            fontSize: '2rem',
            fontWeight: 800,
            fontFamily: 'monospace',
            letterSpacing: '0.05em'
          }}>
            {minutes}:{seconds}
          </span>
        </div>

        <div style={{
          fontSize: '0.82rem',
          color: '#94a3b8',
          marginBottom: '1.5rem',
          background: 'rgba(56, 189, 248, 0.08)',
          border: '1px solid rgba(56, 189, 248, 0.2)',
          padding: '0.65rem 0.85rem',
          borderRadius: '10px',
          textAlign: 'left',
          lineHeight: 1.45,
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'flex-start'
        }}>
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>🛡️</span>
          <span><b>Borradores protegidos:</b> Si estás redactando una evolución médica, tus notas están guardadas localmente y se restaurarán de forma segura.</span>
        </div>

        <button
          onClick={continueSession}
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            padding: '0.85rem 1.75rem',
            borderRadius: '10px',
            fontWeight: 700,
            fontSize: '1rem',
            cursor: 'pointer',
            width: '100%',
            boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)',
            transition: 'all 0.2s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.6)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'none';
            e.currentTarget.style.boxShadow = '0 4px 14px rgba(16, 185, 129, 0.4)';
          }}
        >
          Mantener Sesión Activa
        </button>
      </div>
    </div>
  );
};

function App() {
  const location = useLocation();
  const isLogin = location.pathname === '/login';
  const isRegister = location.pathname === '/register';
  const isForgotPassword = location.pathname === '/forgot-password';
  const isUpdatePassword = location.pathname === '/update-password';
  const isEmailConfirmed = location.pathname === '/email-confirmed';
  const isPublicTracking = location.pathname.startsWith('/track/');

  // Custom White-Label Logic
  const hostname = window.location.hostname.toLowerCase();
  const isMainDomain = 
    hostname === 'localhost' || 
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('trazapp.com') || 
    hostname.endsWith('trazapp.ar') || 
    hostname.endsWith('vercel.app') ||
    /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname);
  const isCustomDomain = !isMainDomain;

  // Routes
  const isTenantLogin = /^\/[^/]+\/login$/.test(location.pathname) || (isCustomDomain && isLogin);
  const isPatientPortal = /^\/[^/]+\/portal$/.test(location.pathname) || (isCustomDomain && location.pathname === '/portal');
  const isTenantLanding = (isCustomDomain && location.pathname === '/') || (!isCustomDomain && /^\/[^/]+$/.test(location.pathname) && ![
    '/login', '/register', '/forgot-password', '/update-password', '/email-confirmed',
    '/admin', '/shipping', '/crops', '/rooms', '/genetics', '/clones', '/devices',
    '/laboratory', '/extractions', '/insumos', '/providers', '/stock', '/settings',
    '/compras', '/expenses', '/metrics', '/informes', '/dispensary', '/account',
    '/patients', '/templates', '/appointments', '/growy-dashboard', '/dashboard-v2', '/room-v2', '/crops-v2'
  ].includes(location.pathname));

  // Consider Tenant Login and Landing as public routes for styling
  const isPublicRoute = isLogin || isRegister || isForgotPassword || isUpdatePassword || isEmailConfirmed || isPublicTracking || isTenantLogin || isTenantLanding || /^\/[^/]+\/apply$/.test(location.pathname);

  const hideAdminChrome = isPublicRoute || isPatientPortal;

  return (
    <OrganizationProvider>
      <DataProvider>
        <SidebarProvider>
          <AppContent
            isPublicRoute={isPublicRoute}
            isPatientPortal={isPatientPortal}
            hideAdminChrome={hideAdminChrome}
            isCustomDomain={isCustomDomain}
          />
        </SidebarProvider>
      </DataProvider>
    </OrganizationProvider>
  );
}

interface AppContentProps {
  isPublicRoute: boolean;
  isPatientPortal: boolean;
  hideAdminChrome: boolean;
  isCustomDomain: boolean;
}

const AppContent: React.FC<AppContentProps> = ({
  isPublicRoute,
  isPatientPortal,
  hideAdminChrome,
  isCustomDomain
}) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();

  return (
    <div className={`App ${isPublicRoute || isPatientPortal ? 'public-theme' : ''}`}>
      <ClickSpark
        sparkColor="#03fc41"
        sparkSize={5}
        sparkRadius={20}
        sparkCount={8}
        duration={500}
        easing="ease-in-out"
        extraScale={0.9}
      />
      {!hideAdminChrome && <GuidedTour />}
      {!hideAdminChrome && <Sidebar />}
      {/* Temporarily hidden per user request */}
      {/* {!hideAdminChrome && <ChatWidget />} */}
      {/* New Growy Assistant */}
      {!hideAdminChrome && <GrowyOrb />}

      {/* New Mobile High-End Glass Bottom Navigation */}
      {!hideAdminChrome && <MobileBottomBar />}

      <SystemBroadcastBanner />

      {/* Session Timeout Warning Modal */}
      <SessionTimeoutWarning />

      <Routes>
        <Route path="/login" element={isCustomDomain ? <TenantLogin /> : <Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />
        <Route path="/email-confirmed" element={<EmailConfirmed />} />
        <Route path="/track/:id" element={<PublicTracking />} />
        <Route path="/invite/:token" element={<PatientOnboarding />} />
        
        {/* White-Label Custom Domain Roots */}
        {isCustomDomain && (
          <>
            <Route path="/" element={<TenantLanding />} />
            <Route path="/portal" element={
              <RequireAuth>
                <PatientPortal />
              </RequireAuth>
            } />
          </>
        )}

        {/* White-Label Fallback Slug Routes */}
        <Route path="/:slug" element={<TenantLanding />} />
        <Route path="/:slug/login" element={<TenantLogin />} />
        <Route path="/:slug/apply" element={<TenantApply />} />
        <Route path="/:slug/portal" element={
          <RequireAuth>
            <PatientPortal />
          </RequireAuth>
        } />            

        {/* Protected Routes Wrapped in Main Content */}
        {!isCustomDomain && (
          <Route path="/" element={
            <RequireAuth>
              <KYCGuard>
                <MainContent>
                  <Dashboard />
                </MainContent>
              </KYCGuard>
            </RequireAuth>
          } />
        )}

        <Route path="/dashboard-v2" element={
          <RequireAuth>
            <KYCGuard>
              <MainContent>
                <DashboardV2 />
              </MainContent>
            </KYCGuard>
          </RequireAuth>
        } />

        <Route path="/room-v2" element={
          <RequireAuth>
            <KYCGuard>
              <MainContent>
                <RoomDetailV2 />
              </MainContent>
            </KYCGuard>
          </RequireAuth>
        } />

        <Route path="/crops-v2" element={
          <RequireAuth>
            <KYCGuard>
              <MainContent>
                <CropsV2 />
              </MainContent>
            </KYCGuard>
          </RequireAuth>
        } />

        <Route path="/shipping" element={
          <RequireAuth>
            <MainContent>
              <div style={{ padding: '2rem', color: '#fff' }}><h2>Envíos</h2><p>Módulo de envíos en construcción...</p></div>
            </MainContent>
          </RequireAuth>
        } />

        <Route path="/admin" element={
          <RequireAuth>
            <KYCGuard>
              <MainContent>
                <AdminDashboard />
              </MainContent>
            </KYCGuard>
          </RequireAuth>
        } />

        <Route path="/admin/clients" element={
          <RequireAuth>
            <KYCGuard>
              <MainContent>
                <ClientManagement />
              </MainContent>
            </KYCGuard>
          </RequireAuth>
        } />

        <Route path="/admin/monitoring" element={
          <RequireAuth>
            <KYCGuard>
              <MainContent>
                <SystemMonitoring />
              </MainContent>
            </KYCGuard>
          </RequireAuth>
        } />

        <Route path="/admin/devices" element={
          <RequireAuth>
            <KYCGuard>
              <MainContent>
                <DeviceInventory />
              </MainContent>
            </KYCGuard>
          </RequireAuth>
        } />

        {/* GROWER / CULTIVO MODULES */}
        <Route path="/crops" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <Crops />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/crops/:id" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <CropDetail />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/rooms" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <Rooms />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/rooms/:id" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <RoomDetail />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/genetics" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <Genetics />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/genetics/rd" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <GeneticsRD />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/genetic/:id" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <GeneticDetail />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/clones" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <Clones />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/clones/:id" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <BatchDetail />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/devices" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <Devices />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/laboratory" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['medico']}>
              <KYCGuard>
                <MainContent>
                  <LaboratoryPage />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/extractions" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['medico']}>
              <KYCGuard>
                <MainContent>
                  <Extractions />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/insumos" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <Insumos />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/providers" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'grower', 'staff']}>
              <KYCGuard>
                <MainContent>
                  <InsumoProviders />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        {/* FINANCE / ADMIN MODULES */}
        <Route path="/stock" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'grower']}>
              <KYCGuard>
                <MainContent>
                  <Stock />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/settings" element={
          <RequireAuth>
            <RoleGuard allowedRoles={[]}>
              <KYCGuard>
                <MainContent>
                  <Settings />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/compras" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin']}>
              <KYCGuard>
                <MainContent>
                  <Compras />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/expenses" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin']}>
              <KYCGuard>
                <MainContent>
                  <Expenses />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/metrics" element={
          <RequireAuth>
            <RoleGuard allowedRoles={[]}>
              <KYCGuard>
                <MainContent>
                  <Metrics />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/informes" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'grower', 'staff', 'medico']}>
              <KYCGuard>
                <MainContent>
                  <Informes />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        {/* MEDICAL / PATIENTS MODULES */}
        <Route path="/dispensary" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'medico']}>
              <KYCGuard>
                <MainContent>
                  <Dispensary />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/account" element={
          <RequireAuth>
            <MainContent>
              <AccountInfo />
            </MainContent>
          </RequireAuth>
        } />

        <Route path="/patients" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'medico']}>
              <KYCGuard>
                <MainContent>
                  <Patients />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/patients/:id" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'medico']}>
              <KYCGuard>
                <MainContent>
                  <PatientDetail />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />
        <Route path="/templates" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'medico']}>
              <KYCGuard>
                <MainContent>
                  <Templates />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/appointments" element={
          <RequireAuth>
            <RoleGuard allowedRoles={['admin', 'medico', 'grower', 'staff', 'owner']}>
              <KYCGuard>
                <MainContent>
                  <Appointments />
                </MainContent>
              </KYCGuard>
            </RoleGuard>
          </RequireAuth>
        } />

        <Route path="/growy-dashboard" element={
          <RequireAuth>
            <KYCGuard>
              <MainContent>
                <GrowyDashboard />
              </MainContent>
            </KYCGuard>
          </RequireAuth>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

export default App;
